import React, {useEffect, useId} from 'react';
import {Box, Input} from "@chakra-ui/react";

interface LanguageInputProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  languages: string[];
  selectedLanguage: string;
  onSelectLanguage: (lang: string) => void;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string;
}

const LanguageInput: React.FC<LanguageInputProps> = (
  {
    isOpen,
    setIsOpen,
    languages,
    selectedLanguage,
    onSelectLanguage,
    onChange,
    placeholder,
    label
  }: LanguageInputProps) => {
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
  const baseId: string = useId();
  const listId: string = `${baseId}-list`;
  const optionId = (index: number): string => `${baseId}-option-${index}`;

  const filteredLanguages: string[] = languages.filter((lang: string) =>
    lang.toLowerCase().includes(selectedLanguage.toLowerCase())
  );

  // Filtering can shrink the list past the highlight, which would leave aria-activedescendant
  // pointing at an id that no longer exists and Enter selecting undefined.
  useEffect(() => {
    setHighlightedIndex((current: number) => (current >= filteredLanguages.length ? -1 : current));
  }, [filteredLanguages.length]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({block: "nearest"});
    }
  }, [highlightedIndex]);

  const cancelPendingClose = (): void => {
    if (closeTimerRef.current === null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  useEffect(() => () => {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
  }, []);

  // Reopening must cancel the deferred blur-close, or the previous selection's timer fires
  // ~100ms later and shuts the list the user just reopened.
  const openList = (): void => {
    cancelPendingClose();
    setIsOpen(true);
  };

  const selectLanguage = (language: string) => {
    cancelPendingClose();
    onSelectLanguage(language);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  return (
    <Box position="relative" w="100%">
      {label && (
        <div className="flex mb-2 base2 font-semibold">
          {label}
        </div>)
      }
      <Input
        h={"50px"}
        p={2}
        autoFocus={false}
        role="combobox"
        aria-expanded={isOpen && filteredLanguages.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={label ?? placeholder}
        aria-activedescendant={highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
        placeholder={placeholder}
        value={selectedLanguage}
        onChange={(e) => {
          openList();
          onChange(e.target.value);
        }}
        onFocus={openList}
        onClick={openList}
        onBlur={() => {
          // Delay to allow click on dropdown items
          cancelPendingClose();
          closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setIsOpen(false);
            // Unmounting the list without clearing the highlight leaves aria-activedescendant
            // pointing at an id that no longer exists in the DOM.
            setHighlightedIndex(-1);
          }, 100);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && filteredLanguages.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, filteredLanguages.length - 1)
            );
          } else if (e.key === "ArrowUp" && filteredLanguages.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            const highlightedLanguage: string | undefined = filteredLanguages[highlightedIndex];
            if (highlightedLanguage !== undefined) selectLanguage(highlightedLanguage);
          } else if (e.key === "Escape" && isOpen) {
            // Stop here or headlessui's Dialog sees the same Escape and closes the whole modal,
            // losing every selection the user made just to dismiss a dropdown.
            e.stopPropagation();
            cancelPendingClose();
            setIsOpen(false);
            setHighlightedIndex(-1);
          }
        }}
        color="white"
        border="1px solid gray"
        _focus={{borderColor: "brandPurple.500"}}
        sx={{
          border: "none",
          outline: "none",
          boxShadow: "inset 0 0 0 1px gray",
          color: "white",
          _focus: {
            boxShadow: "inset 0 0 0 2px var(--chakra-colors-brandPurple-500)",
          },
          _hover: {
            boxShadow: "inset 0 0 0 1px var(--chakra-colors-brandPurple-500)",
          },
        }}
      />
      {isOpen && filteredLanguages.length > 0 && (
        <Box
          id={listId}
          role="listbox"
          aria-label={label ?? placeholder}
          position="absolute"
          zIndex="10"
          bg="n.700"
          mt={1}
          w="100%"
          maxH="200px"
          overflowY="auto"
          borderWidth="1px"
          borderColor="n.500"
          borderRadius="lg"
          boxShadow="0 1rem 2rem rgba(0, 0, 0, 0.55)"
          overflowX="hidden"
        >
          {filteredLanguages.map((lang: string, index: number) => (
            <Box
              key={lang}
              id={optionId(index)}
              role="option"
              aria-selected={highlightedIndex === index}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              px={4}
              py={2}
              _hover={{bg: "brandPurple.500", cursor: "pointer"}}
              bg={highlightedIndex === index ? "brandPurple.500" : ""}
              color="white"
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={() => {
                selectLanguage(lang);
              }}
            >
              {lang}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default LanguageInput;
